<script>
import { mapActions, mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import LoadingState from '../../../../components/widgets/LoadingState.vue';
import DatePicker from 'vue2-datepicker';
import { getCentralHeaders } from 'dashboard/helpers/centralApiHeaders';
import {
  offsetMinutesFor,
  shiftedNow,
  todayAndNow,
  filterPastDates,
  filterPastTimeSlots as dropPastTimeSlots,
} from 'dashboard/helper/appointmentPastSlots';

export default{
  components:{
    LoadingState,
    DatePicker
  },
  props: {
    presetContactId: {
      type: [Number, String],
      default: null,
    },
    presetContactName: {
      type: String,
      default: '',
    },
    presetConversationId: {
      type: [Number, String],
      default: null,
    },
  },
  data(){
    return{
      selectedAutoDate: null,

      isLoading: false,
      isDropdownOpen: false,
      ids: null,
      selectedDate: null,
      startTime: null,
      serviceDuration: null,
      requestFeedback: false,
      selectedBranchId: null,
      selectedServiceId: null,
      selectedStaffId: null,
      staffList: [],
      selectedTimezone: "",
      timezones: null,

      selectedMonth: null,
      availableDates: null,
      isDatesLoading: false,
      availableTimes: null,
      isTimesLoading: false,
      selectedTime: null,
      chatList: null,
      reminders: [],
      timeUnits: [
          { value: 'minutes', label: this.$t('APPOINTMENTS.CREATE_MODAL.REMINDERS.MINUTES'), multiplier: 1/60 },
          { value: 'hours', label: this.$t('APPOINTMENTS.CREATE_MODAL.REMINDERS.HOURS'), multiplier: 1 },
          { value: 'days', label: this.$t('APPOINTMENTS.CREATE_MODAL.REMINDERS.DAYS'), multiplier: 24 }
      ],

      contactCount: 0,
      contactCurrentPage: 1,
      contactPerPage: 0,
      contactTotalPages: 0,
      contactNextPage: false,
      contactQuery: '',
      selectedCustomer: null,

      dropdownRef: null,
    }
  },
  computed: {
    userTimezone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    },
    // The picked timezone's UTC offset, read off the same `timezones` payload
    // formatTimezone() already uses (a current timestamp per zone, ending in
    // e.g. "+08:00"). Falls back to the browser's own offset before that
    // payload has loaded.
    selectedTimezoneOffsetMinutes() {
      return offsetMinutesFor(this.timezones, this.selectedTimezone);
    },
    // Live current instant, shifted onto the selected timezone's wall clock
    // (read back via the UTC getters below) so "past" filtering matches
    // whichever timezone was picked in the form, not the browser's own.
    nowInSelectedTimezone() {
      return shiftedNow(this.selectedTimezoneOffsetMinutes);
    },
    todayDateString() {
      return todayAndNow(this.selectedTimezoneOffsetMinutes).today;
    },
    branchList() {
      return this.$store.state.branchV2.allBranches || [];
    },
    serviceList() {
      return this.$store.state.serviceV2.allBranchServices || [];
    },
    isAutoAssignStaff() {
      return this.selectedStaffId === 'autoAssign';
    },
    isCustomerLocked() {
      return !!this.presetContactId;
    },
    getCurrentAccountId() {
      return this.$store.getters['getCurrentAccountId'];
    },
    getSelectedCustomerName() {
      if (!this.ids) return this.$t('APPOINTMENTS.CREATE_MODAL.SELECT_CUSTOMER');
      const selected = this.chatList.find(c => c.id === this.ids.customerId);
      return selected?.name || this.$t('APPOINTMENTS.CREATE_MODAL.SELECT_CUSTOMER');
    },
    missingFields() {
      const missing = [];
      if(this.isAutoAssignStaff){
        if (!this.selectedTimezone) missing.push('Timezone');
        if (!this.ids?.customerId) missing.push('Customer');
        if (!this.ids?.conversationId) missing.push('Conversation');
        if (!this.selectedAutoDate) missing.push('Date');
        if (!this.startTime) missing.push('Start time');
        if (!this.endTime) missing.push('End time');
        if (!this.selectedServiceId) missing.push('Service');
        if (!this.selectedStaffId) missing.push('Staff');
      } else {
        if (!this.selectedTimezone) missing.push('Timezone');
        if (!this.ids?.customerId) missing.push('Customer');
        if (!this.ids?.conversationId) missing.push('Conversation');
        if (!this.selectedDate) missing.push('Date');
        if (!this.startTime) missing.push('Start time');
        if (!this.endTime) missing.push('End time');
        if (!this.selectedServiceId) missing.push('Service');
      }
      return missing;
    },
    isFormValid() {
      return this.missingFields.length === 0;
    }
  },
  watch: {
    async contactCurrentPage(){
      await this.fetchChatLists();
    },
    async selectedAutoDate(newDate){
      if (!this.selectedServiceId) return;
      const payload = {
        branch_id: this.selectedBranchId,
        service_id: this.selectedServiceId,
        date: newDate,
        client_timezone: this.selectedTimezone
      };
      this.selectedTime = null;
      this.isTimesLoading = true;
      try {
        const slots = await this.$store.dispatch('appointments/createAutoAssignTimeSlots', payload);
        this.availableTimes = this.filterPastTimeSlots(slots, newDate, 'start_time');
      } catch (err) {
        console.error(err);
        useAlert(this.$t('APPOINTMENTS.CREATE_MODAL.ERRORS.TIMESLOTS_LOAD_ERROR'));
        this.availableTimes = [];
      } finally {
        this.isTimesLoading = false;
      }
    },
    async contactQuery(){
      this.contactCurrentPage = 1;
      await this.fetchChatLists();
    },
    async selectedBranchId(newBranchId) {
      if (newBranchId) {
        this.selectedServiceId = null;
        this.selectedStaffId = 'autoAssign';
        this.staffList = [];
        this.availableDates = null;
        this.isDatesLoading = false;
        this.availableTimes = null;
        this.isTimesLoading = false;
        this.selectedDate = null;
        this.selectedMonth = null;
        this.selectedTime = null;

        await this.fetchAllBranchServices(newBranchId);
      }
    },
    async selectedServiceId(newServiceId) {
      if (newServiceId) {
        this.selectedStaffId = 'autoAssign';
        this.staffList = [];

        const selectedService = this.serviceList.find(item => item.service.id === newServiceId);
        this.staffList = selectedService
          ? selectedService.staff.filter(staff =>
              staff.branches.some(branch => branch.id === this.selectedBranchId)
            )
          : [];
      } else {
        this.staffList = [];
      }
      this.availableDates = null;
      this.isDatesLoading = false;
      this.availableTimes = null;
      this.isTimesLoading = false;
      this.selectedDate = null;
      this.selectedMonth = null;
      this.selectedTime = null;

      // Auto-assign uses selectedAutoDate (not selectedDate), so it won't re-trigger
      // the selectedAutoDate watcher when service changes. Re-fetch timeslots manually
      // if a date was already picked.
      if (this.isAutoAssignStaff && this.selectedAutoDate && newServiceId) {
        const payload = {
          branch_id: this.selectedBranchId,
          service_id: newServiceId,
          date: this.selectedAutoDate,
          client_timezone: this.selectedTimezone
        };
        this.isTimesLoading = true;
        try {
          const slots = await this.$store.dispatch('appointments/createAutoAssignTimeSlots', payload);
          this.availableTimes = this.filterPastTimeSlots(slots, this.selectedAutoDate, 'start_time');
        } catch (err) {
          console.error(err);
          useAlert(this.$t('APPOINTMENTS.CREATE_MODAL.ERRORS.TIMESLOTS_LOAD_ERROR'));
          this.availableTimes = [];
        } finally {
          this.isTimesLoading = false;
        }
      }
    },
      async selectedStaffId(newStaffId) {
          if (newStaffId) {
              this.availableDates = null;
              this.isDatesLoading = false;
              this.availableTimes = null;
              this.isTimesLoading = false;
              this.selectedDate = null;
              this.selectedMonth = null;
              this.selectedTime = null;
          }
      },
      async selectedMonth(newMonth) {
          if (newMonth) {
              this.availableDates = null;
              this.isDatesLoading = false;
              this.availableTimes = null;
              this.isTimesLoading = false;
              this.selectedDate = null;
              this.selectedTime = null;

              await this.checkAvailableDates(newMonth);
          }
      },
      async selectedDate(newDate) {
        if (newDate) {
          this.isTimesLoading = true;
          this.selectedTime = null;

          await this.checkAvailableTimes(newDate);
        }
      },
      async selectedTime(newTime) {
        if (newTime && !this.isAutoAssignStaff) {
          this.startTime = `${newTime.schedule_time}:00`;
          this.endTime = this.addMinutes(this.startTime, newTime.duration);
        } else if (newTime && this.isAutoAssignStaff) {
          this.startTime = newTime.start_time;
          this.endTime = newTime.end_time;
        } else {
          this.startTime = null;
          this.endTime = null;
        }
      }
  },
  methods:{
      ...mapActions('appointments', [
        'fetchAvailableDates', 
        'fetchAvailableTimes',
        'getTimezones',
      ]),
      ...mapActions('appointmentsV2', [
        'createAppointment',
      ]),
      ...mapActions('branchV2', [
        'fetchAllBranches',
      ]),
      ...mapActions('serviceV2', [
        'fetchAllBranchServices',
      ]),
      nextPage() {
        if (this.contactNextPage) {
          this.contactCurrentPage++;
        }
      },
      prevPage() {
        if (this.contactCurrentPage > 1) {
          this.contactCurrentPage--;
        }
      },
      addHours(timeStr, hoursToAdd) {
        let [hh, mm, ss] = timeStr.split(":").map(Number);

        // Convert the time string to total minutes
        let totalMinutes = hh * 60 + mm + hoursToAdd * 60;

        // Convert back to hh:mm:ss
        let newH = Math.floor(totalMinutes / 60) % 24;
        let newM = Math.floor(totalMinutes % 60);

        return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
      },
      addMinutes(timeStr, minutesToAdd) {
        let [hh, mm, ss] = timeStr.split(":").map(Number);

        // Add the minutes
        let totalMinutes = hh * 60 + mm + minutesToAdd;

        // Convert back to hh:mm:ss
        let newH = Math.floor(totalMinutes / 60) % 24;
        let newM = totalMinutes % 60;

        return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
      },
      async fetchChatLists() {
        try {
            const url = new URL(`${window.location.origin}/api/v1/accounts/${this.getCurrentAccountId}/appointments/contacts`);
            url.searchParams.append('page', this.contactCurrentPage);
            url.searchParams.append('q', this.contactQuery);

            const rawResponse = await fetch(url.toString(), {
                headers: {
                    ...getCentralHeaders(),
                }
            });

            if (!rawResponse.ok) {
                const errorData = await rawResponse.json();
                throw new Error(errorData.detail);
            }

            const response = await rawResponse.json();
            this.chatList = response.contacts;
            this.contactCount = response.meta.count;
            this.contactPerPage = response.meta.per_page;
            this.contactTotalPages = response.meta.total_pages;
            this.contactNextPage = response.meta.next;
        } catch (err) {
            console.error('Error fetching contacts:', err);
            useAlert(this.$t('APPOINTMENTS.CREATE_MODAL.ERRORS.CONTACTS_LOAD_ERROR'));
            this.chatList = [];
        }
    },
      // Shared by the staff-schedule flow (schedule_time) and the auto-assign
      // flow (start_time): drops slots already in the past when the picked
      // date is today, per the selected timezone's clock.
      filterPastTimeSlots(slots, dateStr, timeField) {
          const { today, nowHm } = todayAndNow(this.selectedTimezoneOffsetMinutes);
          return dropPastTimeSlots(slots, dateStr, timeField, today, nowHm);
      },
      disablePastMonths(date) {
          const now = this.nowInSelectedTimezone;
          const startOfCurrentMonth = new Date(now.getUTCFullYear(), now.getUTCMonth(), 1);
          return date < startOfCurrentMonth;
      },
      async checkAvailableDates(month){
          if (!this.selectedServiceId) return;
          const query = {
              branch_id: this.selectedBranchId,
              staff_id: this.selectedStaffId,
              year: month.split('-')[0],
              month: month.split('-')[1],
              client_timezone: this.selectedTimezone
          };
          this.isDatesLoading = true;
          try{
              const dates = await this.fetchAvailableDates(query);
              this.availableDates = filterPastDates(
                dates.available_dates,
                this.todayDateString
              );
          }catch(err){
              console.error(err);
              useAlert(err);
              this.availableDates = [];
          }finally{
              this.isDatesLoading = false;
          }
      },
      async checkAvailableTimes(selectedDate){
          if (!this.selectedServiceId) return;
          const query = {
              branch_id: this.selectedBranchId,
              staff_id: this.selectedStaffId,
              service_id: this.selectedServiceId,
              date: selectedDate,
              client_timezone: this.selectedTimezone
          };
          this.isTimesLoading = true;
          try{
              const time = await this.fetchAvailableTimes(query);
              this.availableTimes = this.filterPastTimeSlots(time.time_slots, selectedDate, 'schedule_time');
          }catch(err){
              console.error(err);
              useAlert(err);
              this.availableTimes = [];
          }finally{
              this.isTimesLoading = false;
          }
      },
      getDayOfWeek(dateString) {
          const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday" ];
          const date = new Date(dateString);
          return days[date.getDay()];
      },
      addReminder() {
          this.reminders.push({
              customValue: 30,
              unit: 'minutes'
          });
      },
      removeReminder(index) {
          this.reminders.splice(index, 1);
      },
      convertToHours(value, unit) {
          const unitConfig = this.timeUnits.find(u => u.value === unit);
          return value * unitConfig.multiplier;
      },
      async handleSubmit(){
        if(!this.isFormValid){
          useAlert(this.$t('APPOINTMENTS.CREATE_MODAL.ALERTS.VALIDATION_ERROR'));
          return;
        }
        try{
            this.isLoading = true;
            if(this.isAutoAssignStaff){
              const payload = {
                "branch_id": this.selectedBranchId,
                "client_timezone": this.selectedTimezone,
                "customer_id": this.ids.customerId,
                "conversation_id": this.ids.conversationId,
                "date": this.selectedAutoDate,
                "end_time": this.endTime,
                "request_feedback": this.requestFeedback,
                "service_id": this.selectedServiceId,
                "start_time": this.startTime
              }
              await this.$store.dispatch('appointments/createAutoAssignAppointment', payload);
            }else {
              const payload = {
                "client_timezone": this.selectedTimezone,
                "customer_id": this.ids.customerId,
                "conversation_id": this.ids.conversationId,
                "date": this.selectedDate,
                "end_time": this.endTime,
                "branch_id": this.selectedBranchId,
                // "request_feedback": this.requestFeedback,
                "service_id": this.selectedServiceId,
                "staff_id": this.selectedStaffId,
                "start_time": this.startTime,
                // "reminders": this.reminders.map(r => this.convertToHours(r.customValue, r.unit)),
                // "status": "confirmed"
              };
              await this.createAppointment(payload);
            }
            
            // Add labels to the conversation if a conversation ID exists
            if (this.ids.conversationId) {
              try {
                // Get branch name and service name
                const selectedBranch = this.branchList.find(item => item.branch.id === this.selectedBranchId);
                const selectedService = this.serviceList.find(item => item.service.id === this.selectedServiceId);

                if (selectedBranch && selectedService) {
                  const branchName = selectedBranch.branch.name;
                  const serviceName = selectedService.service.name;
                  
                  // Add labels to the conversation
                  await this.$store.dispatch('conversationLabels/addAppointmentLabels', {
                    conversationId: this.ids.conversationId,
                    branchName,
                    serviceName
                  });
                }
              } catch (labelError) {
                console.error('Failed to add labels to conversation:', labelError);
                // Continue with success message even if labeling fails
            }
            }
            useAlert(this.$t('APPOINTMENTS.CREATE_MODAL.SUCCESS.APPOINTMENT_CREATED'));
            this.$emit('refresh');
            this.$emit('close');
        }catch(err){
            console.error(err);
            useAlert(err);
        }finally{
            this.isLoading = false;
        }
      },
      formatTimezone(timezone, timestamp) {
          const cityRaw = timezone?.split?.("/")[1] || timezone;
          const city = cityRaw?.replace(/_/g, " ") || "";

          if (!timestamp) return city;

          const match = timestamp.match(/([+-]\d{2}:\d{2})$/);
          const offset = match ? match[1] : "+00:00";
          return `${city} (UTC${offset})`;
      },
      selectCustomer(customer) {
        this.selectedCustomer = customer;
        this.ids = {
          customerId: customer.id,
          conversationId: customer.conversations[0].id
        };
        this.contactQuery = customer.name;
        this.isDropdownOpen = false;
      },
      closeDropdownWithDelay() {
        setTimeout(() => {
          this.isDropdownOpen = false;
        }, 200);
      },
      showInboxLogo(inbox) {
          switch(inbox) {
              case 'website':
                  return 'globe';
              case 'facebook':
                  return 'brand-facebook'; 
              case 'twitter':
                  return 'brand-twitter';
              case 'whatsapp':
                  return 'brand-whatsapp';
              case 'api':
                  return 'code';
              case 'email':
                  return 'mail';
              case 'telegram':
                  return 'brand-telegram';
              case 'line':
                  return 'brand-line';
              case 'sms':
                  return 'brand-sms';
              case 'phone':
                  return 'call';
              case 'instagram':
                  return 'brand-instagram';
              default:
                  return 'globe';
          }
      }
  },
  async mounted(){
      try{
          this.isLoading = true;
          await this.fetchAllBranches();
          if (this.isCustomerLocked) {
            this.ids = {
              customerId: this.presetContactId,
              conversationId: this.presetConversationId,
            };
            this.chatList = [];
          } else {
            await this.fetchChatLists();
          }
          this.timezones = await this.getTimezones();
          this.selectedTimezone = this.userTimezone;
          
          // Initialize reminders with default value from localStorage
          const defaultReminderValue = localStorage.getItem('defaultReminderValue');
          if (defaultReminderValue) {
              const value = parseInt(defaultReminderValue);
              // Convert the stored hours value to minutes for better UX
              this.reminders = [{
                  customValue: value * 60,
                  unit: 'minutes'
              }];
          }
          // If no default reminder, use 30 minutes as default
          if (this.reminders.length === 0) {
              this.reminders = [{
                  customValue: 30,
                  unit: 'minutes'
              }];
          }
      }catch(err){
          console.error(err);
          useAlert(err);
      }finally{
          this.isLoading = false;
      }
  }
}
</script>

<template>
    <div data-testid="create_appt_modal" class="p-6 border-2 rounded-lg bg-white dark:bg-slate-800 shadow-lg max-w-md w-full mx-auto max-h-[100vh] overflow-y-auto">
        <div v-if="!isLoading">
          <label v-if="isCustomerLocked" class="block relative mb-4">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_CUSTOMER') }}
              </span>
              <div
                class="mt-1 w-full flex items-center gap-2 px-3 py-2 border rounded-md text-slate-900 dark:text-white bg-slate-100 dark:bg-slate-700 dark:border-slate-600"
              >
                <fluent-icon icon="person" size="14" />
                <span>{{ presetContactName || presetContactId }}</span>
              </div>
              <p class="mt-1 text-xs text-slate-400 dark:text-slate-500">
                {{ $t('APPOINTMENTS.CREATE_MODAL.CUSTOMER_LOCKED_HINT') }}
              </p>
          </label>
          <label v-else class="block relative mb-4">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_CUSTOMER') }}
              </span>
              <div
                class="mt-1 w-full border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus-within:ring-2 focus-within:ring-woot-500"
              >
                <input
                  type="text"
                  data-testid="create_appt_customer_input"
                  v-model="contactQuery"
                  class="reset-base w-full p-2 bg-transparent border-none focus:outline-none"
                  :placeholder="this.$t('APPOINTMENTS.CREATE_MODAL.SELECT_CUSTOMER')"
                  @focus="isDropdownOpen = true"
                  @blur="closeDropdownWithDelay"
                  @click="isDropdownOpen = true"
                />
                <div
                    v-if="isDropdownOpen" 
                    class="fixed inset-0 z-40"
                    @click="isDropdownOpen = false"
                ></div>
                <div 
                  v-if="isDropdownOpen" 
                  class="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-slate-700 border rounded-md shadow-lg max-h-60 overflow-y-auto"
                >
                  <div v-if="chatList.length === 0" class="p-2 text-slate-500">
                    {{ $t('APPOINTMENTS.CREATE_MODAL.NO_CUSTOMERS') }}
                  </div>
                  <div v-else>
                    <div
                      v-for="customer in chatList"
                      :key="customer.id"
                      data-testid="create_appt_customer_option"
                      :data-id="customer.id"
                      class="p-3 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer customer-option border-b border-slate-200 dark:border-slate-700"
                      @click.prevent="selectCustomer(customer)"
                    >
                      <div class="text-sm font-medium text-slate-900 dark:text-white flex gap-1 items-center">
                        
                        <fluent-icon
                          v-if="customer.conversations && customer.conversations.length !== 0"
                          size="14"
                          :icon="showInboxLogo(customer.conversations[0].inbox_channel)"
                        /> 
                       
                        {{ customer.name }}
                        <span v-if="customer.phone_number" class="text-xs text-slate-500 dark:text-slate-400">
                          ({{ customer.phone_number }})
                        </span>
                      </div>
                      
                      <div
                        class="text-xs text-slate-500 dark:text-slate-400"
                        v-if="customer.conversations && customer.conversations.length !== 0"
                      >
                        {{ customer.conversations[0].inbox_name || '—' }}
                      </div> 
                     
                    </div>

                    <div class="flex items-center justify-between p-2 border-t">
                      <button
                        type="button"
                        class="px-2 py-1 text-sm bg-slate-200 dark:bg-slate-600 rounded disabled:opacity-50"
                        @mousedown.prevent="prevPage"
                        :disabled="contactCurrentPage === 1"
                      >
                        Previous
                      </button>
                      <span class="text-sm text-slate-600 dark:text-slate-400">
                        {{ contactCurrentPage }} / {{ contactTotalPages }}
                      </span>
                      <button
                        type="button"
                        class="px-2 py-1 text-sm bg-slate-200 dark:bg-slate-600 rounded disabled:opacity-50"
                        @mousedown.prevent="nextPage"
                        :disabled="!contactNextPage"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <p v-if="!ids || !ids.customerId" class="mt-1 text-xs text-red-500 dark:text-red-400">
                {{ $t('APPOINTMENTS.CREATE_MODAL.REQUIRED.CUSTOMER') }}
              </p>
            </label>

            <label class="block mb-4">
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_BRANCH') }}</span>
                <select
                    data-testid="create_appt_branch_select"
                    class="reset-base mb-0 w-full border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:ring-2 focus:ring-woot-500 focus:outline-none max-h-40 overflow-y-auto"
                    v-model="selectedBranchId"
                    :disabled="!branchList"
                    :class="{'hover:cursor-not-allowed': disabled}"
                >
                    <option v-if="branchList.length === 0">{{ $t('APPOINTMENTS.CREATE_MODAL.NO_BRANCHES') }}</option>
                    <option :value="null" disabled>{{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_BRANCH') }}</option>
                    <option v-for="item in branchList" :key="item.branch.id" :value="item.branch.id">
                        {{ item.branch.name }}
                    </option>
                </select>
                <p v-if="!selectedBranchId" class="mt-1 text-xs text-red-500 dark:text-red-400">
                  {{ $t('APPOINTMENTS.CREATE_MODAL.REQUIRED.BRANCH') }}
                </p>
            </label>

            <div class="grid grid-cols-2 gap-4 items-start mb-4">
                <label class="block">
                    <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_SERVICE') }}</span>
                    <select
                        data-testid="create_appt_service_select"
                        class="reset-base mb-0 w-full border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:ring-2 focus:ring-woot-500 focus:outline-none max-h-40 overflow-y-auto"
                        v-model="selectedServiceId"
                        :disabled="!selectedBranchId"
                        :class="{'hover:cursor-not-allowed': !selectedBranchId}"
                    >
                        <option v-if="serviceList && serviceList.length === 0" disabled>{{ $t('APPOINTMENTS.CREATE_MODAL.NO_SERVICES') }}</option>
                        <option :value="null" disabled>{{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_SERVICE') }}</option>
                        <option v-for="item in serviceList" :key="item.service.id" :value="item.service.id">
                            {{ item.service.name }}
                        </option>
                    </select>
                    <p v-if="selectedBranchId && !selectedServiceId" class="mt-1 text-xs text-red-500 dark:text-red-400">
                      {{ $t('APPOINTMENTS.CREATE_MODAL.REQUIRED.SERVICE') }}
                    </p>
                </label>

                <label v-if="staffList" class="block">
                    <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_STAFF') }}</span>
                    <select
                        data-testid="create_appt_staff_select"
                        class="reset-base mb-0 w-full border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:ring-2 focus:ring-woot-500 focus:outline-none max-h-40 overflow-y-auto"
                        v-model="selectedStaffId"
                        :disabled="!selectedBranchId"
                        :class="{'hover:cursor-not-allowed': !selectedBranchId}"
                    >
                        <option v-if="staffList && staffList.length === 0" disabled>{{ $t('APPOINTMENTS.CREATE_MODAL.NO_STAFF') }}</option>
                        <!-- <option :value="null" disabled>{{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_STAFF') }}</option> -->
                        <option value="autoAssign">{{ $t('APPOINTMENTS.CREATE_MODAL.AUTO_ASSIGN_STAFF') }}</option>
                        <option v-for="staff in staffList" :key="staff.id" :value="staff.id">
                            {{ staff.name }}
                        </option>
                    </select>
                </label>
            </div>

            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_TIMEZONE') }}</span>
            <multiselect 
              data-testid="create_appt_timezone"
              v-model="selectedTimezone"
              :options="Object.keys(timezones)"
              :custom-label="(key) => formatTimezone(key, timezones[key])"
              :placeholder="$t('APPOINTMENTS.CREATE_MODAL.SELECT_TIMEZONE')"
              :searchable="true"
              :close-on-select="true"
              :show-labels="false"
              class="w-full text-slate-900 dark:text-white dark:bg-slate-700"
              :class="{'hover:cursor-not-allowed': !selectedBranchId}"
              :disabled="!selectedBranchId"
            />
            <p v-if="selectedBranchId && !selectedTimezone" class="mt-1 text-xs text-red-500 dark:text-red-400">
              {{ $t('APPOINTMENTS.CREATE_MODAL.REQUIRED.TIMEZONE') }}
            </p>

            <label class="block mb-4" v-if="!isAutoAssignStaff">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_MONTH') }}
              </span>

              <div
                class="mt-1"
                :class="{
                  'opacity-50 cursor-not-allowed': !selectedTimezone || !selectedServiceId
                }"
              >
                <DatePicker
                  data-testid="create_appt_month_picker"
                  v-model="selectedMonth"
                  type="month"
                  format="YYYY-MM"
                  value-type="format"
                  :editable="false"
                  :disabled="!selectedTimezone || !selectedServiceId"
                  :disabled-date="disablePastMonths"
                  :placeholder="$t('APPOINTMENTS.CREATE_MODAL.SELECT_MONTH')"
                  class="w-full"
                />
              </div>
              <p v-if="selectedTimezone && selectedServiceId && !selectedMonth" class="mt-1 text-xs text-red-500 dark:text-red-400">
                {{ $t('APPOINTMENTS.CREATE_MODAL.REQUIRED.MONTH') }}
              </p>
            </label>

            <label class="block mb-4" v-if="isAutoAssignStaff">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_DATE') }}</span>
              <input
                type="date"
                data-testid="create_appt_auto_date_input"
                v-model="selectedAutoDate"
                :min="todayDateString"
                :disabled="!selectedTimezone || !selectedServiceId"
                :class="[
                  'reset-base mt-1 w-full px-3 py-2 border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:ring-2 focus:ring-woot-500 focus:outline-none',
                  { 
                    'opacity-50': !selectedTimezone || !selectedServiceId,
                    'hover:cursor-not-allowed': !selectedTimezone || !selectedServiceId
                  }
                ]"
              />
              <p v-if="selectedTimezone && selectedServiceId && !selectedAutoDate" class="mt-1 text-xs text-red-500 dark:text-red-400">
                {{ $t('APPOINTMENTS.CREATE_MODAL.REQUIRED.DATE') }}
              </p>
            </label>

            <div class="grid grid-cols-2 gap-4 items-center">
              <label class="block" v-if="!isAutoAssignStaff">
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_DATE') }}</span>
                <div class="mt-1">
                  <p v-if="availableDates === null" class="text-sm text-slate-400 dark:text-slate-500 italic">
                    {{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_MONTH_FIRST') }}
                  </p>
                  <p v-else-if="isDatesLoading" class="text-sm text-slate-400 dark:text-slate-500 italic">
                    {{ $t('APPOINTMENTS.CREATE_MODAL.LOADING_DATES') }}
                  </p>
                  <p v-else-if="availableDates.length === 0" data-testid="create_appt_no_dates" class="text-sm text-red-500 dark:text-red-400 italic">
                    {{ $t('APPOINTMENTS.CREATE_MODAL.NO_AVAILABLE_DATES') }}
                  </p>
                  <select
                    v-else
                    data-testid="create_appt_date_select"
                    class="w-full border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:ring-2 focus:ring-woot-500 focus:outline-none max-h-40 overflow-y-auto"
                    v-model="selectedDate"
                  >
                    <option v-for="date in availableDates" :key="date" :value="date">{{ `${date}, ${getDayOfWeek(date)}` }}</option>
                  </select>
                </div>
                <p v-if="availableDates && availableDates.length > 0 && !selectedDate" class="mt-1 text-xs text-red-500 dark:text-red-400">
                  {{ $t('APPOINTMENTS.CREATE_MODAL.REQUIRED.DATE') }}
                </p>
              </label>

              <label class="block" v-if="!isAutoAssignStaff">
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_TIME') }}
                </span>
                <div class="mt-1">
                  <p v-if="isTimesLoading" class="text-sm text-slate-400 dark:text-slate-500 italic">
                    {{ $t('APPOINTMENTS.CREATE_MODAL.LOADING_TIMES') }}
                  </p>
                  <p v-else-if="availableTimes === null" class="text-sm text-slate-400 dark:text-slate-500 italic">
                    {{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_DATE_FIRST') }}
                  </p>
                  <p v-else-if="availableTimes.length === 0" data-testid="create_appt_no_times" class="text-sm text-red-500 dark:text-red-400 italic">
                    {{ $t('APPOINTMENTS.CREATE_MODAL.NO_AVAILABLE_TIMES') }}
                  </p>
                  <select
                    v-else
                    data-testid="create_appt_time_select"
                    v-model="selectedTime"
                    class="w-full border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:ring-2 focus:ring-woot-500 focus:outline-none max-h-40 overflow-y-auto"
                  >
                    <option v-for="time in availableTimes" :key="time.schedule_time" :value="time">{{ time.schedule_time }}</option>
                  </select>
                </div>
                <p v-if="availableTimes && availableTimes.length > 0 && !selectedTime" class="mt-1 text-xs text-red-500 dark:text-red-400">
                  {{ $t('APPOINTMENTS.CREATE_MODAL.REQUIRED.TIME') }}
                </p>
              </label>

              <label class="block col-span-2" v-if="isAutoAssignStaff">
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_TIME') }}</span>
                <div class="mt-1">
                  <p v-if="isTimesLoading" class="text-sm text-slate-400 dark:text-slate-500 italic">
                    {{ $t('APPOINTMENTS.CREATE_MODAL.LOADING_TIMES') }}
                  </p>
                  <p v-else-if="availableTimes === null" class="text-sm text-slate-400 dark:text-slate-500 italic">
                    {{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_DATE_FIRST') }}
                  </p>
                  <p v-else-if="availableTimes.length === 0" data-testid="create_appt_no_times" class="text-sm text-red-500 dark:text-red-400 italic">
                    {{ $t('APPOINTMENTS.CREATE_MODAL.NO_AVAILABLE_TIMES') }}
                  </p>
                  <select
                    v-else
                    data-testid="create_appt_time_select"
                    v-model="selectedTime"
                    class="w-full border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:ring-2 focus:ring-woot-500 focus:outline-none max-h-40 overflow-y-auto"
                  >
                    <option v-for="time in availableTimes" :key="time.start_time" :value="time">{{ time.start_time }}</option>
                  </select>
                </div>
                <p v-if="availableTimes && availableTimes.length > 0 && !selectedTime" class="mt-1 text-xs text-red-500 dark:text-red-400">
                  {{ $t('APPOINTMENTS.CREATE_MODAL.REQUIRED.TIME') }}
                </p>
              </label>
            </div>

            <!-- Reminder section commented out but updated for reference
            <div class="mb-4">
                <div class="flex justify-between w-full mb-2">
                    <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('APPOINTMENTS.CREATE_MODAL.REMINDERS.TITLE') }}</span>
                    <woot-button 
                        size="tiny"
                        variant="smooth"
                        @click="addReminder"
                    >{{ $t('APPOINTMENTS.CREATE_MODAL.REMINDERS.ADD_REMINDER') }}</woot-button>
                </div>

                <div
                    v-for="(reminder, index) in reminders"
                    :key="index"
                    class="flex items-center gap-4"
                >
                <input
                    type="number"
                    v-model.number="reminder.customValue"
                    min="1"
                    class="w-24 border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600"
                />
                <select
                    v-model="reminder.unit"
                    class="w-32 border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600"
                >
                    <option
                        v-for="unit in timeUnits"
                        :key="unit.value"
                        :value="unit.value"
                    >
                        {{ unit.label }}
                    </option>
                </select>
                <button
                    v-if="reminders.length > 1"
                    @click="removeReminder(index)"
                    type="button"
                >
                    <fluent-icon
                        icon="delete"
                        class="text-red-500 hover:text-red-700 cursor-pointer mb-4"
                    />
                </button>
                </div>
            </div> -->

            <div class="flex justify-end gap-2">
                <woot-button data-testid="create_appt_cancel" variant="smooth" color-scheme="secondary" @click="$emit('close')">
                    {{ $t('APPOINTMENTS.CREATE_MODAL.CANCEL') }}
                </woot-button>
                <woot-button data-testid="create_appt_submit" variant="smooth" color-scheme="primary" @click="handleSubmit" :disabled="!isFormValid">
                    {{ $t('APPOINTMENTS.CREATE_MODAL.CREATE') }}
                </woot-button>
            </div>
        </div>
        <div v-else>
            <LoadingState />
        </div>
    </div>
</template>

<style>
.mx-input {
  margin-bottom: 0 !important;
}
</style>
